import { Chart, registerables } from 'chart.js';
import { AppFooter, Col, Header, Spinner2, SplashPage } from 'components';
import GloryBoardWeeklyWinnerCelebration from 'components/GloryBoardWeeklyWinnerCelebration';
import { HOW_TO_EARN_REWARDS_PATH, isWebsite as functionIsWebsite } from 'core';
import { CTA, loginWithToken, ws } from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

// WEBSITE SCREEN COMPONENTS
import Admin from 'screens/Admin';
import Login from 'screens/Login';
import Rerouter from 'screens/Rerouter';
import { useSaveLanguageChoice } from './saveLanguageChoice';
import SignUp from 'screens/SignUp';
import {
  AthleteZone,
  Conference,
  Event,
  FanZone,
  GetTheMobileApp,
  Landing,
  News,
  NewsArticle,
  OurAthletes,
  OurStory,
  Partners
} from 'screens/Website';
import {
  ChildSafetyStandard,
  CommunityGuidelines,
  PrivacyPolicy,
  StopTheHate,
  TermsAndConditions
} from 'screens/Website/FooterPages';

import { Footer as WebsiteFooter, Header as WebsiteHeader } from 'screens/Website/Home/components';

// APP SCREEN COMPONENTS
import Billing from 'screens/Billing';
import DeepLink from 'screens/DeepLink';
import FanCamChallenge from 'screens/FanCamChallenge';
import P4PDay from 'screens/P4PDay';
import Browse from 'screens/Browse';
import Dashboard from 'screens/Dashboard';
import ForgotPassword from 'screens/ForgotPassword';
import GetTheApp from 'screens/GetTheApp';
import Home from 'screens/Home';
import Messages from 'screens/Messages';
import Notifications from 'screens/Notifications';
import Onboarding from 'screens/Onboarding';
import Post from 'screens/Post';
import PostsFeed from 'screens/PostsFeed';
import Profile from 'screens/Profile';
import CTAScreenV2 from 'screens/CTAScreenV2';
import RateIt from 'screens/RateIt';
import Settings from 'screens/Settings';
import Shoutout from 'screens/Shoutout';
import { Products, Store } from 'screens/Store/screens';
import Subscribe from 'screens/Subscribe';
import { PaymentFailure, PaymentSuccess } from 'screens/SubscriptionResult';
import PaymentMethodSelection from 'screens/PaymentMethodSelection';
import { VerifyEmail } from 'screens/ValidateEmail/screens';
import SubscriptionPlansScreen from 'screens/SubscriptionPlans';
import EmailView from 'screens/EmailView';
import OpenApp from 'screens/OpenApp';

// ADMIN SUB ROUTES
import {
  AddArticle,
  AddEvent,
  AddPartner,
  AddTeam,
  Articles,
  Events,
  Games,
  GameDayLab,
  Orders,
  Partners as AdminPartners,
  Posts,
  Pricing,
  Products as AdminProducts,
  Reports,
  Fancams,
  Insights,
  Tiri,
  Shoutouts,
  Stories,
  Teams,
  Updates,
  Users,
  GeneralSettings,
  AdminSubscription,
  SubscriptionRedirect,
  AdminRevenueSplit,
  AdminActivityLog,
  AdminAuditLogs,
  AdminStripeRequests,
  AdminWebhookLogs,
  LiveStatsEngagement,
  QuickQuiz,
  FanWave,
  GoalRush,
  Email,
  EmailDashboard,
  EmailRedirect,
  EmailTemplates,
  EmailSetup,
  EmailReports,
  EmailBanner,
  EmailSettings,
  Polls,
  Moderation
} from 'screens/Admin/screens';
import { isGameDayLabAvailable } from 'screens/Admin/screens/GameDayLab/availability';

/*
  charts.js requires charts to be registered app wide
*/
Chart.register(...registerables);

// USER ROUTES
const User = () => {
  const [showGetTheApp, setShowGetTheApp] = useState(true),
    appState = useSelector((state) => state.appState),
    user = useSelector((state) => state.user),
    homeData = useSelector((state) => state.home),
    location = useLocation().pathname,
    dispatch = useDispatch(),
    navigate = useNavigate(),
    isWebsite = functionIsWebsite(location),
    // CTA flow screens (/shoutout/:username/...) render full-screen without the
    // app header; the trailing slash keeps the /shoutout feed unaffected
    isCtaFlow = location.startsWith('/shoutout/'),
    // The Rate It landing is full-screen too. Kept as its own flag rather than folded into
    // `isCtaFlow`, because that one also gates the Onboarding bypass below and the two flows
    // should stay individually greppable.
    isRateIt = location.startsWith('/rate-it'),
    // An email's "Open the App" and "View in browser" pages are the whole screen as well.
    isEmailLanding =
      location === '/open-app' ||
      location === `/${HOW_TO_EARN_REWARDS_PATH}` ||
      location === '/email/view',
    hideAppHeader = isCtaFlow || isRateIt || isEmailLanding,
    scrollRef = useRef(),
    loading = appState.loadingButton === 'navigator';

  // The fan's explicit language choice, saved to the account so emails can use it.
  useSaveLanguageChoice({ user, appState, dispatch });

  useEffect(() => {
    if (user.fromSignup) {
      dispatch({
        type: 'UPDATE_USER',
        payload: { fromSignup: false }
      });
    }

    ws.connect();
    loginWithToken();

    if (appState.loggedInOnce) return;

    dispatch({
      type: 'UPDATE_APP_STATE',
      payload: { loggedInOnce: true, hideBottomNav: false }
    });

    if (user.navigateTo) {
      navigate(`/${user.navigateTo}`);

      dispatch({
        type: 'UPDATE_USER',
        payload: { navigateTo: null }
      });

      return;
    }

    if (user.admin) return navigate('/admin');

    if (user?.atheleteUsernameToFace2Face) {
      navigate(`/profile/${user?.atheleteUsernameToFace2Face}/face2face`);
    } else if (user?.atheleteUsernameToAdPlayaz) {
      navigate(`/profile/${user?.atheleteUsernameToAdPlayaz}/adPlayaz`);
    } else if (user?.athleteUsernameToShoutout) {
      navigate(`/profile/${user?.athleteUsernameToShoutout}/${user?.followUpAction}`);
    } else if (user?.athleteUsernameToFollow) {
      navigate(`/profile/${user?.athleteUsernameToFollow}`);
    } else if (user?.atheleteIdToSubscribe) {
      navigate(`/subscription-plans?teamId=${user?.atheleteIdToSubscribe}`);
    } else if (user?.fromAtheleteUsername) {
      navigate(`/profile/${user?.fromAtheleteUsername}`);
    } else {
      navigate(user.type === 'athlete' ? '/dashboard' : '/home');
    }

    // no need for dependencies since we only to run this once, on component load
  }, []);

  useEffect(() => {
    if (!scrollRef || !isWebsite) return;

    scrollRef?.current?.scrollTo({ top: 0 });
  }, [location]);

  // Social signups from the CTA flow skip onboarding (no username). While that
  // flow is active this session (ctaFlowPath set), let them browse the app
  // freely instead of forcing the generic Onboarding screen — it still shows
  // for username-less users outside a CTA session (e.g. after a hard refresh).
  // The email landings are exempt too: a fan who has not finished onboarding on the web still has
  // to be able to open the app, or read the email, from the link they tapped.
  if (
    !user.username?.length &&
    !isCtaFlow &&
    !isRateIt &&
    !isEmailLanding &&
    !appState.ctaFlowPath
  ) {
    // if (user.followingCount > 0) {
    //   return <Onboarding athleteIdToFollow={user?.following[0]} />;
    // }

    return <Onboarding />;
  }

  return (
    <React.Fragment>
      <SplashPage visible={!homeData} />

      {homeData && (
        <React.Fragment>
          {isWebsite ? <WebsiteHeader /> : !hideAppHeader && <Header />}

          <Routes>
            <Route path="/" element={<Landing />} />

            <Route path="subscription-plans" element={<SubscriptionPlansScreen />} />

            <Route path="athlete-zone" element={<AthleteZone />} />

            <Route path="fan-zone" element={<FanZone />} />

            <Route path="our-athletes" element={<OurAthletes />} />

            <Route path="partners-and-sponsors" element={<Partners />} />

            <Route path="our-story" element={<OurStory />} />

            <Route path="event" element={<Event />} />

            <Route path="rerouter" element={<Rerouter />} />

            <Route path="news" element={<News />} />

            <Route path="signup" element={<SignUp />} />

            <Route path="login" element={<Login />} />

            <Route path="terms-and-conditions" element={<TermsAndConditions />} />

            <Route path="privacy-policy" element={<PrivacyPolicy />} />

            <Route path="child-safety-standard-policy" element={<ChildSafetyStandard />} />

            <Route path="community-guidelines" element={<CommunityGuidelines />} />

            <Route path="stop-the-hate" element={<StopTheHate />} />

            <Route path="forgot-password" element={<ForgotPassword />} />

            <Route path="/get-the-mobile-app" element={<GetTheMobileApp />} />

            {/* Declared in both containers; see NotUser.js. */}
            <Route path="/open-app" element={<OpenApp />} />
            <Route
              path={`/${HOW_TO_EARN_REWARDS_PATH}`}
              element={<OpenApp appPath={HOW_TO_EARN_REWARDS_PATH} />}
            />

            <Route path="/email/view" element={<EmailView />} />

            {/*
              <Route path='signup'>
                <Redirect to='/home' />
              </Route>

              <Route path='login'>
                <Redirect to='/home' />
              </Route>
            */}

            <Route path="articles/:articleId" element={<NewsArticle />} />

            <Route path="/conference/:roomId" element={<Conference />} />

            <Route path="post/:postId" element={<Post />} />

            <Route path="shared/post/:postId" element={<Post />} />

            <Route path="/news" element={<News />} />

            <Route path="rerouter" element={<Rerouter />} />

            <Route path="articles/:articleId" element={<NewsArticle />} />

            <Route path="/home" element={<Home />} />

            <Route path="/posts/:id?" element={<PostsFeed />} />

            <Route path="/products/:id?" element={<Products />} />

            <Route path="/browse" element={<Browse />} />

            <Route path="/messages" element={<Messages />} />

            <Route path="/messages/:userId" element={<Messages />} />

            <Route path="/profile" element={<Profile userId={user._id} />} />

            <Route path="/shoutout" element={<Shoutout />} />

            <Route
              path="/shoutout/:username/*"
              element={<CTAScreenV2 ctaType={CTA.SEND_SHOUTOUT} />}
            />

            {/* Also in `NotUser.js` — see the note there. */}
            <Route path="/rate-it/:tiriId/*" element={<RateIt />} />

            <Route path="/store" element={<Store />} />

            <Route path="/settings" element={<Settings />} />

            <Route path="/billing" element={<Billing />} />

            <Route path="/subscribe/:athleteId" element={<Subscribe />} />

            <Route path="/subscribe" element={<Subscribe />} />

            <Route path="/subscription/success" element={<PaymentSuccess />} />

            <Route path="/subscription/cancel" element={<PaymentFailure />} />

            <Route path="verify-email/:emailToken" element={<VerifyEmail />} />

            <Route path="/payment-method-selection" element={<PaymentMethodSelection />} />

            {user.type === 'athlete' && <Route path="/dashboard" element={<Dashboard />} />}

            <Route path="/l/:shortCode" element={<DeepLink />} />

            {/* Where a campaign email's primary button lands. Declared in both containers:
                a fan arriving from an email is usually signed out, and a route present in only
                one of the two works for half the audience and 404s for the other half. */}
            <Route path="/fancam/:id" element={<FanCamChallenge />} />

            <Route path="/p4pday" element={<P4PDay />} />

            <Route path="/profile/:username/:cta/:uuid" element={<Profile />} />

            <Route path="/profile/:username/:cta" element={<Profile />} />

            <Route path="/profile/:username" element={<Profile />} />

            {(user.admin || user.type === 'team') && (
              <Route path="/admin" element={<Admin />}>
                <Route
                  path="/admin"
                  element={user.admin ? <Users /> : <Navigate to="/admin/quick-quiz" replace />}
                />

                <Route path="quick-quiz" element={<QuickQuiz />} />

                <Route path="polls" element={<Polls />} />

                {/* Channel owners manage their own channel's GoalRush rules here, like polls. */}
                <Route path="goalrush" element={<GoalRush />} />

                {user.admin && (
                  <>
                    <Route path="articles" element={<Articles />} />

                    <Route path="articles/:articleId" element={<NewsArticle />} />

                    <Route path="articles/add-article" element={<AddArticle />} />

                    <Route path="articles/edit/:articleId" element={<AddArticle />} />

                    <Route path="partners" element={<AdminPartners />} />

                    <Route path="add-partner" element={<AddPartner />} />

                    <Route path="teams" element={<Teams />} />

                    <Route path="add-team" element={<AddTeam />} />

                    <Route path="events" element={<Events />} />

                    <Route path="add-event" element={<AddEvent />} />

                    <Route path="event-updates" element={<Updates />} />

                    <Route path="pricing" element={<Pricing />} />

                    <Route path="users" element={<Users />} />

                    <Route path="posts" element={<Posts />} />

                    <Route path="products" element={<AdminProducts />} />

                    <Route path="reports" element={<Reports />} />

                    <Route path="live-stats-engagement" element={<LiveStatsEngagement />} />

                    <Route path="stories" element={<Stories />} />

                    <Route path="fancams" element={<Fancams />} />
                    <Route path="insights" element={<Insights />} />
                    <Route path="tiri" element={<Tiri />} />

                    <Route path="shoutouts" element={<Shoutouts />} />

                    <Route path="games" element={<Games />} />

                    <Route
                      path="game-day-lab"
                      element={
                        isGameDayLabAvailable() ? <GameDayLab /> : <Navigate to="/admin" replace />
                      }
                    />

                    <Route path="orders" element={<Orders />} />

                    <Route path="general-settings" element={<GeneralSettings />} />

                    <Route path="fanwave" element={<FanWave />} />

                    {/*
                      The Email Manager, as the design's frames arrange it: the manager opens on the
                      audience, `View Templates` opens the catalog, and `Create Campaign` opens
                      `setup` — one page carrying the template editor, the campaign builder and the
                      scheduler, because that is the order the work is actually done in.

                      Nested rather than flat so the selected template and the audience filters
                      survive the move between pages — the filters are what a campaign stores, and
                      losing them on navigation would mean re-picking an audience to schedule it.

                      `audience` and `compose` were pages until the merge and are linked from
                      outside the app, so they redirect rather than 404.
                    */}
                    <Route path="email" element={<Email />}>
                      <Route index element={<EmailDashboard />} />
                      <Route path="templates" element={<EmailTemplates />} />
                      <Route path="setup" element={<EmailSetup />} />
                      <Route path="reports" element={<EmailReports />} />
                      <Route path="settings" element={<EmailSettings />} />
                      <Route path="banner" element={<EmailBanner />} />
                      {/* Each was the route for one of these pages at some point, and links to
                          them exist in the wild. */}
                      <Route path="audience" element={<EmailRedirect to="/admin/email" />} />
                      <Route path="compose" element={<EmailRedirect to="/admin/email/setup" />} />
                      <Route path="campaigns" element={<EmailRedirect to="/admin/email/setup" />} />
                      <Route
                        path="analytics"
                        element={<EmailRedirect to="/admin/email/reports" />}
                      />
                    </Route>

                    <Route path="subscription" element={<AdminSubscription />} />
                    <Route
                      path="subscription-plans"
                      element={<SubscriptionRedirect defaultTab="plans" />}
                    />
                    <Route
                      path="subscriptions"
                      element={<SubscriptionRedirect defaultTab="overview" />}
                    />

                    <Route path="revenue/split-report" element={<AdminRevenueSplit />} />
                    <Route path="audit-logs" element={<AdminAuditLogs />} />
                    <Route path="stripe-requests" element={<AdminStripeRequests />} />

                    <Route path="reporting/webhook-logs" element={<AdminWebhookLogs />} />

                    <Route path="activity-log" element={<AdminActivityLog />} />

                    <Route path="moderation" element={<Moderation />} />
                  </>
                )}
              </Route>
            )}
          </Routes>

          <Notifications />
          <GloryBoardWeeklyWinnerCelebration />
          <GetTheApp />

          {isWebsite && <WebsiteFooter />}
        </React.Fragment>
      )}

      {loading && (
        <Col
          id="bepis"
          pos="fixed"
          wid="100vw"
          ht="100vh"
          top="0"
          left="0"
          bg="rgba(255, 255, 255, 0.5)"
          zIndex="99"
          centerAll
        >
          <Spinner2 />
        </Col>
      )}

      {showGetTheApp && !isWebsite && !isCtaFlow && !isRateIt && !isEmailLanding && (
        <AppFooter handleClose={() => setShowGetTheApp(false)} />
      )}
    </React.Fragment>
  );
};

export default User;
